/**
 * Schedule agenda days
 * Builds the day list the Agenda layout renders: each day's items plus the organizer's summary for
 * that day, so a day can show with a summary and no timed items (or only summaries at all).
 */

import type { ScheduleDaySummary } from "../../_types";
import type { ScheduleItemRow } from "../types/db-types";

/** One day in the Agenda layout. */
export interface AgendaDay {
  /** `YYYY-MM-DD`. */
  date: string;
  /** The organizer's summary for the day, trimmed, or null when there is none. */
  summary: string | null;
  items: ScheduleItemRow[];
}

interface BuildAgendaDaysOptions {
  /**
   * Add days that have a summary but no items. Off while a label filter is active, so a filter
   * shows only the days with matching items.
   */
  includeSummaryOnlyDays: boolean;
}

/**
 * Merges dated item groups with day summaries into a sorted list of agenda days.
 *
 * @param dateGroups - Items grouped by date (from `groupItemsByDate`); the `null` group is ignored.
 * @param daySummaries - The block's day summaries; blank ones are ignored, and the first wins on a repeated date.
 * @param options - See BuildAgendaDaysOptions.
 * @returns The days, sorted by date.
 * @throws Never.
 */
export function buildAgendaDays(
  dateGroups: { date: string | null; items: ScheduleItemRow[] }[],
  daySummaries: ScheduleDaySummary[] | undefined,
  { includeSummaryOnlyDays }: BuildAgendaDaysOptions
): AgendaDay[] {
  const summaryByDate = new Map<string, string>();
  for (const entry of daySummaries ?? []) {
    const text = entry.text.trim();
    if (!entry.date || !text || summaryByDate.has(entry.date)) continue;
    summaryByDate.set(entry.date, text);
  }

  const days = new Map<string, AgendaDay>();
  for (const group of dateGroups) {
    if (group.date === null) continue;
    days.set(group.date, { date: group.date, summary: summaryByDate.get(group.date) ?? null, items: group.items });
  }
  if (includeSummaryOnlyDays) {
    for (const [date, summary] of summaryByDate) {
      if (!days.has(date)) days.set(date, { date, summary, items: [] });
    }
  }

  // `YYYY-MM-DD` sorts correctly as a string.
  return [...days.values()].sort((a, b) => a.date.localeCompare(b.date));
}
